import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PoweredBy } from "../components/PoweredBy";

const COVER_IMAGE =
  "https://customer-assets.emergentagent.com/job_kisubika-ar-preview/artifacts/g9if9z51_ceb0aa1f-674f-45e2-9842-73fd283714c6.png";

const HOVER_HOLD_MS = 600;
const TRANSITION_MS = 900;

/**
 * KISUBIKA — Art Cover Landing.
 * The supplied artwork already contains the full manifesto (wordmark, tagline,
 * AGE 1 slider, "REAL FACE. REAL PAINT. REAL TRACE." caption and play button).
 * We render it full-bleed and let interaction (hover hold, tap, click, keyboard)
 * trigger a cinematic fade-zoom into /shop where the AR commerce experience lives.
 */
const ArtLanding = () => {
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);
  const hoverTimer = useRef(null);
  const triggered = useRef(false);

  const enterShop = () => {
    if (triggered.current) return;
    triggered.current = true;
    setLeaving(true);
    setTimeout(() => navigate("/shop"), TRANSITION_MS - 50);
  };

  const onHoverStart = () => {
    if (triggered.current) return;
    hoverTimer.current = setTimeout(enterShop, HOVER_HOLD_MS);
  };
  const onHoverEnd = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (["Enter", " ", "ArrowDown", "ArrowRight"].includes(e.key)) enterShop();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="art-landing"
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      onClick={enterShop}
      onTouchStart={enterShop}
      role="button"
      aria-label="Enter KISUBIKA — hover, tap or press enter"
      className="fixed inset-0 z-50 bg-white text-[var(--kb-fg)] overflow-hidden cursor-pointer flex items-center justify-center"
      style={{
        opacity: leaving ? 0 : 1,
        transform: leaving ? "scale(1.06)" : "scale(1)",
        transition: `opacity ${TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), transform ${TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
      }}
    >
      <img
        src={COVER_IMAGE}
        alt="KISUBIKA — 100 Faces. 100 Ages. One Life. A Klein-Blue painted profile of a Ugandan child."
        data-testid="cover-image"
        className="h-full w-full sm:max-h-[100vh] sm:max-w-[100vw] sm:h-auto sm:w-auto object-cover sm:object-contain select-none pointer-events-none"
        draggable={false}
        style={{
          filter: leaving ? "blur(8px)" : "blur(0)",
          transition: `filter ${TRANSITION_MS}ms ease`,
        }}
      />

      {/* Bottom-right hint that quietly disappears on transition */}
      <div
        data-testid="cover-hint"
        className="absolute bottom-3 right-4 kb-mono-label text-[var(--kb-fg-2)] tracking-[0.32em] hidden sm:block"
        style={{
          opacity: leaving ? 0 : 0.55,
          transition: `opacity ${TRANSITION_MS}ms ease`,
        }}
      >
        HOVER · TAP · ENTER
      </div>

      {/* Subtle Pleasurealm credit, bottom-left so it doesn't fight the artwork */}
      <PoweredBy
        subtle
        className="absolute bottom-3 left-4 hidden sm:block"
      />

      {/* Mobile-friendly hint with bouncing arrow */}
      <div
        className="absolute bottom-6 inset-x-0 text-center sm:hidden"
        style={{
          opacity: leaving ? 0 : 1,
          transition: `opacity ${TRANSITION_MS}ms ease`,
        }}
      >
        <div className="inline-flex flex-col items-center gap-2 px-5 py-3 bg-white/90 backdrop-blur border border-[var(--kb-blue)]">
          <div className="kb-mono-label kb-blue tracking-[0.32em]">TAP TO ENTER</div>
          <div className="kb-blue text-lg" style={{ animation: "kbBounce 1.6s ease-in-out infinite" }}>↓</div>
        </div>
      </div>
    </div>
  );
};

export default ArtLanding;
